import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/atoms/button';
import { ConversationDetail } from '@/components/messaging';
import {
  useGetConversation,
  useOpenConversation,
  useGetMessages,
  useSendTextMessage,
  useSendImageMessage,
  useMarkAsRead,
} from '@/lib/api/messaging';

interface ConversationContainerProps {
  orderId: string;
  onImageClick?: (imageUrl: string) => void;
  showBackButton?: boolean;
  onBack?: () => void;
}

export function ConversationContainer({
  orderId,
  onImageClick,
  showBackButton = false,
  onBack,
}: ConversationContainerProps) {
  const { t } = useTranslation();

  const {
    data: conversation,
    isLoading: isLoadingConversation,
    refetch: refetchConversation,
  } = useGetConversation(orderId);

  const openConversation = useOpenConversation(orderId);

  const {
    data: messagesData,
    isLoading: isLoadingMessages,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useGetMessages(orderId);

  const sendMessage = useSendTextMessage(orderId);
  const sendImage = useSendImageMessage(orderId);
  const markAsRead = useMarkAsRead(orderId);

  const messages = messagesData?.messages || [];
  const isSending = sendMessage.isPending || sendImage.isPending;

  // Use ref to track if we've already marked as read for this conversation
  const markedAsReadRef = useRef<string | null>(null);

  useEffect(() => {
    if (
      conversation &&
      conversation.unreadCount > 0 &&
      markedAsReadRef.current !== conversation.id
    ) {
      markedAsReadRef.current = conversation.id;
      markAsRead.mutateAsync(undefined).catch((error) => {
        console.error('Failed to mark messages as read:', error);
        markedAsReadRef.current = null; // Reset so it can retry
      });
    }
  }, [conversation, markAsRead]);

  const handleOpenConversation = useCallback(async () => {
    try {
      await openConversation.mutateAsync({});
      refetchConversation();
    } catch (error) {
      toast.error(t('messaging.openError', 'Impossible d\'ouvrir la conversation'));
    }
  }, [openConversation, refetchConversation, t]);

  const handleSendText = useCallback(
    async (text: string) => {
      try {
        await sendMessage.mutateAsync({ content: text });
      } catch (error) {
        toast.error(t('messaging.sendError', 'Impossible d\'envoyer le message'));
      }
    },
    [sendMessage, t]
  );

  const handleSendImage = useCallback(
    async (file: File) => {
      try {
        await sendImage.mutateAsync({ imageFile: file });
      } catch (error) {
        toast.error(t('messaging.sendImageError', 'Impossible d\'envoyer l\'image'));
      }
    },
    [sendImage, t]
  );

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (isLoadingConversation) {
    return (
      <div className="flex items-center justify-center h-full min-h-[300px]">
        <Loader2 className="size-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!conversation) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[300px] gap-4 p-6">
        <MessageSquare className="size-12 text-muted-foreground" />
        <p className="text-sm text-muted-foreground text-center">
          {t('messaging.noConversation', 'Pas encore de conversation')}
        </p>
        <p className="text-xs text-muted-foreground text-center">
          {t('messaging.startHintSeller', 'Démarrez une conversation avec le client')}
        </p>
        <Button
          onClick={handleOpenConversation}
          disabled={openConversation.isPending}
          className="mt-2"
        >
          {openConversation.isPending ? (
            <>
              <Loader2 className="size-4 mr-2 animate-spin" />
              Chargement...
            </>
          ) : (
            t('messaging.startConversation', 'Démarrer une conversation')
          )}
        </Button>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <ConversationDetail
        conversation={conversation}
        messages={messages}
        isLoadingMessages={isLoadingMessages}
        isLoadingMore={isFetchingNextPage}
        isSending={isSending}
        hasNextPage={hasNextPage}
        onSendText={handleSendText}
        onSendImage={handleSendImage}
        onLoadMore={handleLoadMore}
        onImageClick={onImageClick}
        showBackButton={showBackButton}
        onBack={onBack}
      />
    </div>
  );
}
